<template>
  <b-modal id="upload-special-discount-id-modal" dialog-class="base-modal" title="Senior Citizen / PWD" centered
    @show="resetForm" @hide="resetForm">
    <b-form>
      <b-form-group>
        <b-form-radio-group id="discount-type" v-model="form.discount_type" :options="typeOptions"
          name="radio-options"></b-form-radio-group>
        <b-form-invalid-feedback :force-show="!!(formErrors.discount_type)">
          {{ formErrors.discount_type }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="ID Type" v-if="form.discount_type === 'senior'">
        <b-dropdown id="id-type-dropdown"
          :text="!form.customer_id_type ? formPlaceholders.customer_id_type : form.customer_id_type.text"
          :toggle-class="['text-left bg-transparent', { 'unselected': !form.customer_id_type, 'border-danger': !!(formErrors.customer_id_type) }]"
          menu-class="w-100 p-0" block no-caret>
          <b-dropdown-item v-for="(id, idIndex) in idTypes" :key="`id-${idIndex}`" @click="form.customer_id_type = id"
            :class="{ 'bg-light': form.customer_id_type && form.customer_id_type.value === id.value }">
            {{ id.text }}
          </b-dropdown-item>
        </b-dropdown>
        <b-form-invalid-feedback :force-show="!!(formErrors.customer_id_type)">
          {{ formErrors.customer_id_type }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Full Name">
        <b-form-input v-model="form.customer_id_full_name" :placeholder="formPlaceholders.customer_id_full_name"
          :class="{ 'is-invalid': !!(formErrors.customer_id_full_name) }" maxlength="70"
          @keypress.native="filterAlphaNumericSpace"></b-form-input>
        <b-form-invalid-feedback :force-show="!!(formErrors.customer_id_full_name)">
          {{ formErrors.customer_id_full_name }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="ID Number">
        <b-form-input v-model="form.customer_id_number" :placeholder="formPlaceholders.customer_id_number"
          :class="{ 'is-invalid': !!(formErrors.customer_id_number) }" maxlength="70"
          @keypress.native="filterAlphaNumericSpace"></b-form-input>
        <b-form-invalid-feedback :force-show="!!(formErrors.customer_id_number)">
          {{ formErrors.customer_id_number }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`Upload ${form.discount_type === 'pwd' ? 'PWD' : ''} ID`">
        <FileUploader :templateValue="JSON.stringify(form.customer_id_image_front)" :filetypes="acceptedFileTypes"
          acceptedFileTypesLabel="Only accepts JPG and PNGs" @fileUpdated="setFileUpload" ref="file-uploader"
          :containerClass="{ 'border-danger': !!(formErrors.customer_id_image_front) }"
          :formError="formErrors.customer_id_image_front" />
      </b-form-group>
      <div id="action-helper" class="rounded bg-light">
        <p class="helper-header d-flex align-items-center">
          <img src="@/assets/images/info-neutral.svg" alt="info" class="mr-2">
          {{ helperHeader }}
        </p>
        <p>{{ helperMessage }}</p>
        <b-form-group label="If ID is not approved" label-class="fw-bold">
          <b-dropdown id="action-dropdown"
            :text="!form.rejected_action ? 'Select from list' : form.rejected_action.text"
            :toggle-class="['text-left bg-white', { 'unselected': !form.rejected_action, 'border-danger': !!(formErrors.rejected_action) }]"
            menu-class="w-100 p-0" block no-caret>
            <b-dropdown-item v-for="(action, actionIndex) in actionOptions" :key="`action-${actionIndex}`"
              @click="form.rejected_action = action">
              {{ action.text }}
            </b-dropdown-item>
          </b-dropdown>
          <b-form-invalid-feedback :force-show="!!(formErrors.rejected_action)">
            {{ formErrors.rejected_action }}
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
      <b-form-invalid-feedback :force-show="!!(formErrors.message)">
        {{ formErrors.message }}
      </b-form-invalid-feedback>
    </b-form>
    <template slot="modal-footer">
      <b-button block pill class="w-75 mx-auto" @click="preValidateForm"
        :style="{
          'background-color': webDesign && webDesign.design ? `${webDesign.design.main_color} !important` : null,
          border: webDesign && webDesign.design ? 'none !important' : null,
          color: webDesign && webDesign.design ? `${webDesign.design.button_text_color} !important` : null
        }">Apply</b-button>
    </template>
  </b-modal>
</template>

<script>
import { required, requiredIf, helpers } from "vuelidate/lib/validators";
import FileUploader from "@/components/FileUploader.vue";
import _ from "lodash";
import { mapGetters, mapState } from "vuex";

function initForm() {
  return {
    discount_type: "senior",
    customer_id_type: null,
    customer_id_full_name: null,
    customer_id_number: null,
    customer_id_image_front: null,
    rejected_action: null,
    notes: null
  }
}
const mustBeAlphaNumericSpace = new RegExp(/^[\w ]*$/);

export default {
  name: "UploadSpecialDiscountIdModal",
  components: { FileUploader },
  data() {
    return {
      idTypes: [
        { value: "PASSPORT", text: "Passport" },
        { value: "SSS", text: "SSS" },
        { value: "NBI", text: "NBI Clearance" },
        { value: "GSIS_SSS", text: "GSIS or SSS Umid Card" },
        { value: "IBP_ID", text: "IBP ID" },
        { value: "PRC", text: "Professional Regulation Commission" },
        { value: "TDCC", text: "Theoretical Diving Course Certificate" },
        { value: "POLICECLR", text: "Police Clearance" },
        { value: "BIRTHCERT", text: "Birth Certificate" },
        { value: "SCHOOL_ID", text: "School ID" },
        { value: "SENIOR", text: "Senior Citizen ID" },
        { value: "OWWA", text: "Overseas Workers Welfare Administration" },
        { value: "POSTAL_ID", text: "Postal ID" },
        { value: "GSR", text: "Government Service Record" },
        { value: "BRGYCERT", text: "Barangay Certification" }
      ],
      formPlaceholders: {
        customer_id_type: "Select from list",
        customer_id_full_name: "Juan Dela Cruz",
        customer_id_number: "ABC123"
      },
      acceptedFileTypes: ".jpg, .png, .jpeg",
      validationMessages: {
        required: "This field is required.",
        regex: "may only contain alphanumeric characters."
      },
      fieldValidations: {
        discount_type: ["required"],
        customer_id_type: ["required"],
        customer_id_full_name: ["required", "regex"],
        customer_id_number: ["required", "regex"],
        customer_id_image_front: ["required"],
        rejected_action: ["required"],
        notes: []
      },
      helperHeader: "Your ID is subject to verification",
      helperMessage: "We will process your SC/PWD identification card to verify its authenticity and if it meets the specific requirements.",
      form: initForm(),
    }
  },
  computed: {
    ...mapState("estore", ["webDesign"]),
    ...mapGetters("estore", ["pointsUsed"]),
    specialDiscountTypes() {
      const specialDiscountSettings = this.$store.getters["estore/specialDiscountSettings"];
      return _.get(specialDiscountSettings, "discount_types", []);
    },
    typeOptions() {
      return [
        { value: "senior", text: "Senior Citizen", disabled: !this.specialDiscountTypes.includes("senior") || this.registeredIdTypes.includes("senior") },
        { value: "pwd", text: "PWD", disabled: !this.specialDiscountTypes.includes("pwd") || this.registeredIdTypes.includes("pwd") }
      ];
    },
    registeredIdTypes() {
      return (this.$store.state.customer.specialDiscountList || []).map(({ discount_type }) => discount_type.toLowerCase());
    },
    formErrors() {
      const $v = this.$v.form;
      const apiErrors = this.$store.state.customer.errors;
      const transformFieldLabel = {
        customer_id_full_name: "Full name",
        customer_id_number: "ID number"
      };
      let errors = {};
      Object.keys(this.form).forEach((field) => {
        if ($v.$invalid && $v.$dirty) {
          this.fieldValidations[field].forEach((validation) => {
            if (!$v[field][validation]) errors[field] = `${validation === 'regex' ? transformFieldLabel[field] : ''} ${this.validationMessages[validation]}`;
          });
        }
      });
      if (!_.isEmpty(apiErrors)) {
        errors = { ...errors, ...apiErrors };
      }
      return errors;
    },
    requestHeader() {
      return {
        Authorization: this.$store.state.auth.token,
        "X-Merchant-Token": this.$store.state.merchant.customerPortalToken
      }
    },
    isLoading() {
      return this.$store.state.customer.loading;
    },
    actionOptions() {
      const actionOptions = [
        { value: "proceed", text: "Place my order without discount" },
        { value: "cancel", text: "Cancel my whole order" }
      ];

      if (this.pointsUsed)
        return actionOptions.filter(({ value }) => value != 'proceed');

      return actionOptions;
    }
  },
  validations: {
    form: {
      discount_type: {
        required
      },
      customer_id_type: {
        required: requiredIf(({ discount_type }) => discount_type === 'senior')
      },
      customer_id_full_name: {
        required,
        regex: function regex(value) {
          return !helpers.req(value) || mustBeAlphaNumericSpace.test(value);
        }
      },
      customer_id_number: {
        required,
        regex: function regex(value) {
          return !helpers.req(value) || mustBeAlphaNumericSpace.test(value);
        }
      },
      customer_id_image_front: {
        required
      },
      rejected_action: {
        required
      }
    }
  },
  methods: {
    resetForm() {
      this.$v.form.$reset();
      const availableIdType = () => {
        const availableType = this.typeOptions.filter(({ disabled }) => !disabled);
        if (availableType.length) return availableType[0].value;
        return null;
      };
      this.form = {
        ...initForm(),
        discount_type: availableIdType(),
        customer_id_type: this.idTypes.find(({ value }) => value.toLowerCase() === availableIdType()),
        rejected_action: this.actionOptions[0]
      };
      this.$store.state.customer.errors = null;
    },
    setFileUpload(image) {
      this.form.customer_id_image_front = image;
    },
    preValidateForm() {
      this.$v.form.$touch();
      if (!this.$v.form.$invalid && !this.$refs["file-uploader"].$v.$invalid && !this.isLoading) this.submitForm();
    },
    async submitForm() {
      const { uuid } = this.$store.state.customer.user || null;
      const form = this.form;

      const request = {
        header: this.requestHeader,
        payload: {
          customer_uuid: uuid,
          ...this.form,
          customer_id_type: form.discount_type === "pwd" ? "PWD" : form.customer_id_type.value,
          customer_id_image_front: form.customer_id_image_front.split("base64,").pop(),
          rejected_action: form.rejected_action.value,
        }
      };

      await this.$store.dispatch("customer/uploadSpecialDiscount", request).then(() => {
        if (_.isEmpty(this.formErrors)) {
          this.$bvModal.hide("upload-special-discount-id-modal");

          if (this.$route.name === "Checkout")
            this.$bvModal.show("discount-modal");
          else if (this.$route.name === "Profile")
            this.$emit("fetchSpecialDiscounts");
        }
      });
    },
    filterAlphaNumericSpace(e) {
      if (!mustBeAlphaNumericSpace.test(String.fromCharCode(e.keyCode))) e.preventDefault();
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('~@/assets/styles/basics/_base-modal.scss');

#action-helper {
  padding: 16px 24px;
  margin-top: 24px;
  background: #F8F9F9;

  p {
    font-size: 14px;

    &.helper-header {
      font-size: 16px;
      @extend .mainFontBold;
    }
  }
}

.modal-footer .btn {
  height: 45px;
}
</style>

<style lang="scss">
#upload-special-discount-id-modal {
  form label {
    font-size: 16px;
  }
}

#discount-type {
  .custom-control.custom-control-inline.custom-radio:first-child {
    margin-right: 50px;
  }
  .custom-control-input:checked ~ .custom-control-label::before {
    color: #fff;
    border-color: var(--main-color) !important;
    background-color: var(--main-color) !important;
  }
}

#id-type-dropdown,
#action-dropdown {
  .btn.dropdown-toggle {
    border: solid 1px #D0D3D9;
    height: 45px;
    color: #495057;
    background: url('~@/assets/images/chevron-down.png') no-repeat right;
    background-size: 25px 22px;
    padding-right: 26px;
    font-size: 1rem;

    &.unselected {
      color: #989FAD;
    }
  }

  .dropdown-menu {
    max-height: 200px;
    overflow-y: auto;
    background: #fff;

    .dropdown-item {
      padding: 0.25rem 0.75rem;
    }
  }
}
</style>
